import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutTemplate, Loader2, Copy, Trash2, Coins, Layers, GitFork, Search, Sparkles, Gem, ShieldQuestion } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { TEMPLATE_CATEGORIES } from "@/components/PublishTemplateDialog";
import { ReportDialog } from "@/components/ReportDialog";
import { toast } from "sonner";

const CAT_LABEL = Object.fromEntries(TEMPLATE_CATEGORIES);

function PriceBadge({ price, owned }) {
  if (owned) return <Badge className="bg-emerald-500/90 text-[10px] gap-1" data-testid="ct-owned-badge">✓ Owned</Badge>;
  if (price > 0) return <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-[10px] gap-1"><Gem className="w-3 h-3" /> {price} credits</Badge>;
  return <Badge className="bg-emerald-500/90 text-[10px]">FREE</Badge>;
}

function TemplateCard({ t, onUse, onDelete, onAppeal, busy }) {
  const removed = t.status === "removed";
  const appealed = t.status === "appealed";
  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover flex flex-col" data-testid={`ct-card-${t.id}`}>
      <div className="relative h-28 grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#7c3aed]">
        <span className="text-5xl">{t.cover_emoji || "🎮"}</span>
        <span className="absolute top-2 right-2"><PriceBadge price={t.credit_price} owned={t.owned} /></span>
        {t.mine && <Badge variant="secondary" className="absolute top-2 left-2 text-[10px]">Yours</Badge>}
      </div>
      <div className="p-3.5 flex-1 flex flex-col">
        <div className="font-bold truncate" data-testid={`ct-name-${t.id}`}>{t.name}</div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">by {t.author_name}</div>
        {removed && <Badge className="mt-1.5 w-fit bg-red-500/90 text-[10px] gap-1" data-testid={`ct-removed-${t.id}`}>🛡️ Removed by moderation</Badge>}
        {appealed && <Badge className="mt-1.5 w-fit bg-amber-500/90 text-[10px]" data-testid={`ct-appealed-${t.id}`}>⏳ Appeal under review</Badge>}
        {t.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5 line-clamp-2">{t.description}</p>}
        <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
          {t.ai_built && <Badge className="text-[10px] gap-1 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-0" data-testid={`ct-ai-built-${t.id}`}><Sparkles className="w-3 h-3" /> AI-built</Badge>}
          <Badge variant="secondary" className="text-[10px]">{CAT_LABEL[t.category] || "Other"}</Badge>
          {t.dimension && <Badge variant="secondary" className="text-[10px] uppercase">{t.dimension}</Badge>}
          <span className="flex items-center gap-1"><Layers className="w-3 h-3" /> {t.level_count} levels</span>
          <span className="flex items-center gap-1"><GitFork className="w-3 h-3" /> {t.clones} clones</span>
        </div>
        <div className="mt-3 flex items-center gap-2">
          {removed && onAppeal ? (
            <Button size="sm" onClick={() => onAppeal(t)} className="rounded-full flex-1 bg-gradient-to-r from-amber-500 to-orange-500" data-testid={`ct-appeal-${t.id}`}>
              <ShieldQuestion className="w-4 h-4" /> Appeal removal
            </Button>
          ) : (
            <Button size="sm" onClick={() => onUse(t)} disabled={!!busy}
              className="rounded-full flex-1 bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid={`ct-use-${t.id}`}>
              {busy === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              {t.owned || t.credit_price === 0 ? "Use template" : `Use · ${t.credit_price} cr`}
            </Button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(t)} disabled={busy === `d-${t.id}`}
              className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] p-2" data-testid={`ct-unpublish-${t.id}`} title="Unpublish">
              {busy === `d-${t.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </button>
          )}
          {!t.mine && !onDelete && (
            <ReportDialog targetType="template" targetId={t.id} targetName={t.name} />
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommunityTemplates() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse");
  const [templates, setTemplates] = useState(null);
  const [mine, setMine] = useState(null);
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("popular");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [credits, setCredits] = useState(null);
  const [confirm, setConfirm] = useState(null); // template pending paid confirm
  const [appeal, setAppeal] = useState(null); // template being appealed
  const [appealMsg, setAppealMsg] = useState("");

  const loadBrowse = useCallback(() => {
    const params = { sort };
    if (category !== "all") params.category = category;
    if (q.trim()) params.q = q.trim();
    api.get("/community-templates", { params }).then((r) => setTemplates(r.data.templates || [])).catch(() => setTemplates([]));
  }, [sort, category, q]);

  useEffect(() => { loadBrowse(); }, [loadBrowse]);
  useEffect(() => { api.get("/credits/dashboard").then((r) => setCredits(r.data.credits_remaining)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "mine" && mine === null) api.get("/community-templates/mine").then((r) => setMine(r.data.templates || [])).catch(() => setMine([])); }, [tab, mine]);

  const doClone = async (t) => {
    setBusy(t.id);
    try {
      const { data } = await api.post(`/community-templates/${t.id}/clone`);
      if (data.charged_credits > 0) { toast.success(`Cloned! ${data.charged_credits} credits spent — opening your new game.`); setCredits((c) => (c == null ? c : c - data.charged_credits)); }
      else toast.success("Template cloned into a new game! Opening the builder.");
      loadBrowse();
      navigate(`/app/build/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't use this template");
    } finally { setBusy(""); setConfirm(null); }
  };

  const onUse = (t) => {
    if (!t.owned && t.credit_price > 0) setConfirm(t);
    else doClone(t);
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Unpublish "${t.name}"? It will be removed from Community Templates.`)) return;
    setBusy(`d-${t.id}`);
    try { await api.delete(`/community-templates/${t.id}`); setMine((m) => (m || []).filter((x) => x.id !== t.id)); setTemplates((ts) => (ts || []).filter((x) => x.id !== t.id)); toast.success("Template unpublished."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const submitAppeal = async () => {
    if (!appeal) return;
    setBusy("appeal");
    try {
      await api.post("/rights/appeal", { target_type: "template", target_id: appeal.id, message: appealMsg });
      setMine((m) => (m || []).map((x) => (x.id === appeal.id ? { ...x, status: "appealed" } : x)));
      toast.success("Appeal submitted — an admin will re-review your template.");
      setAppeal(null); setAppealMsg("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="community-templates-page">
      <PageHeader title="Community Templates" icon={LayoutTemplate}
        subtitle="Clone ready-made games from other creators — free or for credits — then make them your own."
        action={credits != null && <Badge variant="secondary" className="gap-1.5 px-3 py-1.5"><Coins className="w-4 h-4 text-amber-400" /> {credits} credits</Badge>} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-5">
          <TabsTrigger value="browse" data-testid="ct-tab-browse">Browse</TabsTrigger>
          <TabsTrigger value="mine" data-testid="ct-tab-mine">My templates</TabsTrigger>
        </TabsList>

        <TabsContent value="browse">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…"
                className="w-full rounded-full border border-[hsl(var(--border))] bg-transparent pl-9 pr-3 py-2 text-sm" data-testid="ct-search" />
            </div>
            <div className="flex gap-1 rounded-full border border-[hsl(var(--border))] p-1">
              {[["popular", "Popular"], ["recent", "Recent"]].map(([k, l]) => (
                <button key={k} onClick={() => setSort(k)} data-testid={`ct-sort-${k}`}
                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${sort === k ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-5" data-testid="ct-categories">
            {[["all", "All"], ...TEMPLATE_CATEGORIES].map(([k, l]) => (
              <button key={k} onClick={() => setCategory(k)} data-testid={`ct-cat-${k}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${category === k ? "bg-[hsl(var(--primary))] text-white border-transparent" : "border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))]/50"}`}>{l}</button>
            ))}
          </div>

          {templates === null ? <Spinner /> : templates.length === 0 ? (
            <EmptyState emoji="🧩" title="No templates here yet" text="Be the first to publish a template from your project page — others can clone it instantly." />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ct-grid">
              {templates.map((t) => <TemplateCard key={t.id} t={t} onUse={onUse} busy={busy} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mine">
          {mine === null ? <Spinner /> : mine.length === 0 ? (
            <EmptyState emoji="🚀" title="You haven't published any templates" text="Open any project → Project & Saves → “Publish as template” to share it with the community."
              action={<Button onClick={() => navigate("/app/games")} className="rounded-full"><Sparkles className="w-4 h-4" /> Go to My Games</Button>} />
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ct-mine-grid">
              {mine.map((t) => <TemplateCard key={t.id} t={t} onUse={onUse} onDelete={t.status === "removed" ? undefined : onDelete} onAppeal={setAppeal} busy={busy} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent data-testid="ct-clone-dialog">
          <DialogHeader>
            <DialogTitle>Use this premium template?</DialogTitle>
            <DialogDescription>
              Cloning <b>{confirm?.name}</b> costs <b>{confirm?.credit_price} credits</b> and pays the creator. You'll get an editable copy with all {confirm?.level_count} levels.
              {credits != null && <span className="block mt-2">Your balance: <b>{credits} credits</b>{credits < (confirm?.credit_price || 0) && <span className="text-[hsl(var(--destructive))]"> — not enough credits.</span>}</span>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setConfirm(null)} data-testid="ct-clone-cancel">Cancel</Button>
            <Button onClick={() => doClone(confirm)} disabled={busy === confirm?.id || (credits != null && credits < (confirm?.credit_price || 0))}
              className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500" data-testid="ct-clone-confirm">
              {busy === confirm?.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gem className="w-4 h-4" />} Spend {confirm?.credit_price} credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!appeal} onOpenChange={(o) => !o && setAppeal(null)}>
        <DialogContent data-testid="ct-appeal-dialog">
          <DialogHeader>
            <DialogTitle>Appeal removal of “{appeal?.name}”</DialogTitle>
            <DialogDescription>
              This template was removed by moderation. Tell us why it should be restored (e.g. it's your original work). An admin will re-review it.
            </DialogDescription>
          </DialogHeader>
          <textarea value={appealMsg} onChange={(e) => setAppealMsg(e.target.value)} maxLength={500} rows={4}
            placeholder="Explain why this is original content you have the rights to…"
            className="w-full rounded-lg border border-[hsl(var(--border))] bg-transparent px-3 py-2 text-sm resize-none" data-testid="ct-appeal-message" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setAppeal(null)} data-testid="ct-appeal-cancel">Cancel</Button>
            <Button onClick={submitAppeal} disabled={busy === "appeal"} className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500" data-testid="ct-appeal-submit">
              {busy === "appeal" ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldQuestion className="w-4 h-4" />} Submit appeal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
